import Link from '@components/common/Link/Link'
import ChevronBackCircleOutline from '@components/icons/ChevronBackCircleOutline/ChevronBackCircleOutline'
import ChevronForwardCircleOutline from '@components/icons/ChevronForwardCircleOutline/ChevronForwardCircleOutline'

import { isMobile } from '@utils/helpers/DeviceUtils'
import clsx from 'clsx'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { FreeMode, Keyboard, Mousewheel, Navigation } from 'swiper/modules'

import { BlogPostFragment } from '@lib/umbraco/generated/types.generated'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperClass } from 'swiper/types'
import s from './BlogPostSlider.module.scss'
import BlogPostSliderItem from './BlogPostSliderItem/BlogPostSliderItem'

export type BlogPostSliderProps = {
  blogPosts: BlogPostFragment[]
  controls?: boolean
  slidesPerView?: number
  spaceBetween?: number
  centeredSlides?: boolean
  hideDescriptions?: boolean
}
const BlogPostSlider: FC<BlogPostSliderProps> = ({
  blogPosts,
  controls,
  spaceBetween,
  slidesPerView,
  centeredSlides,
  hideDescriptions,
}) => {
  if (!slidesPerView || slidesPerView === 0) {
    slidesPerView = 4.5 // can't be 4.5 (then next button thinks this slide is "fully" visible, and skips it)
  }
  if (!spaceBetween || spaceBetween === 0) {
    spaceBetween = 50
  }

  const insufficientSlides = useMemo(
    () => blogPosts?.length < (isMobile() ? 1.5 : slidesPerView),
    [blogPosts, slidesPerView],
  )

  const swiperRef = useRef<SwiperClass | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const [prevEl, setPrevEl] = useState<HTMLElement | null>(null)
  const [nextEl, setNextEl] = useState<HTMLElement | null>(null)

  const [isFirstSlide, setIsFirstSlide] = useState(true)
  const [isLastSlide, setIsLastSlide] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!document) return

    const pressHandler = (e) => {
      if (!rootRef.current) return
      if (!rootRef.current.matches(':hover')) return

      if (e.key === 'ArrowLeft') {
        swiperRef.current.slidePrev()
      }
      if (e.key === 'ArrowRight') {
        swiperRef.current.slideNext()
      }
    }

    // register arrow keyboard key presses on root ref
    document.addEventListener('keydown', pressHandler)
    return () => document.removeEventListener('keydown', pressHandler)
  }, [rootRef, swiperRef])

  return (
    <div ref={rootRef} className={s.root} data-module="BlogPostSlider">
      {controls && (
        <div className="flex justify-end mr-4 md:mr-24 cursor-default mb-4 ">
          <button
            ref={(node) => setPrevEl(node)}
            className={clsx(s.swiperButton, { [s.disabled]: isFirstSlide })}
          >
            <ChevronBackCircleOutline size={35} />
          </button>
          <button
            ref={(node) => setNextEl(node)}
            className={clsx(s.swiperButton, { [s.disabled]: isLastSlide })}
          >
            <ChevronForwardCircleOutline size={35} />
          </button>
        </div>
      )}
      <Swiper
        className={clsx(s.swiperWrapper, {
          // [s.centeredSlides]: insufficientSlides && centeredSlides,
        })}
        slidesPerView={1.5}
        slidesPerGroup={1}
        spaceBetween={30}
        slidesOffsetAfter={insufficientSlides && centeredSlides ? 0 : 30}
        slidesOffsetBefore={insufficientSlides && centeredSlides ? 0 : 30}
        modules={[Keyboard, Mousewheel, FreeMode, Navigation]}
        navigation={{ prevEl, nextEl }}
        onInit={(swiper) => (swiperRef.current = swiper)}
        onSlideChange={(swiper) => {
          swiper.currentBreakpoint >= 1024
            ? setIsFirstSlide(swiper.activeIndex === 0)
            : setIsFirstSlide(swiper.activeIndex === 0)
          swiper.currentBreakpoint >= 1024
            ? setIsLastSlide(
                swiper.activeIndex + Math.floor(slidesPerView) ===
                  swiper.slides.length,
              )
            : setIsLastSlide(swiper.activeIndex === swiper.slides.length - 1)
        }}
        cssMode={true}
        freeMode={false}
        mousewheel={{ forceToAxis: true }}
        grabCursor
        enabled={blogPosts?.length > 1}
        centeredSlides={insufficientSlides && centeredSlides}
        centerInsufficientSlides={insufficientSlides && centeredSlides}
        centeredSlidesBounds={insufficientSlides && centeredSlides}
        touchEventsTarget="container"
        breakpoints={{
          1024: {
            freeMode: {
              enabled: true,
              sticky: false,
            },
            cssMode: false,
            slidesPerView: slidesPerView,
            slidesPerGroup: Math.floor(slidesPerView),
            spaceBetween: spaceBetween,
            slidesOffsetAfter:
              insufficientSlides && centeredSlides ? 0 : spaceBetween,
            slidesOffsetBefore:
              insufficientSlides && centeredSlides ? 0 : spaceBetween,
            enabled: !insufficientSlides,
          },
        }}
      >
        {blogPosts &&
          blogPosts.map((blogPost, index) => (
            <SwiperSlide key={index}>
              {blogPost.linkTo ? (
                <Link link={blogPost.linkTo}>
                  <BlogPostSliderItem
                    blogPost={blogPost}
                    hideDescription={hideDescriptions}
                  />
                </Link>
              ) : (
                <BlogPostSliderItem
                  blogPost={blogPost}
                  hideDescription={hideDescriptions}
                />
              )}
            </SwiperSlide>
          ))}
      </Swiper>
    </div>
  )
}

export default BlogPostSlider
